import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Send } from "lucide-react";
import DOMPurify from "dompurify";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";
import { useTheme } from "../context/ThemeContext";
import { POST_HTML_PURIFY_CONFIG, sanitizeIframeHook, sanitizeAnchorAttrHook } from "../lib/sanitize";

// One-time DOMPurify hook registration — same iframe allow-list used by
// post/custom-page bodies, so admins can embed YouTube on the splash too.
DOMPurify.addHook("uponSanitizeElement", sanitizeIframeHook);
DOMPurify.addHook("uponSanitizeAttribute", sanitizeAnchorAttrHook);

// Size → tailwind class bundles for the splash header. Kept here (not in the
// theme context) because these classes are splash-specific and not reused
// anywhere else on the site.
const SPLASH_HEADER_SIZES = {
  sm: {
    logo: "h-12 w-auto object-contain sm:h-14",
    name: "font-display text-lg font-black tracking-tight sm:text-xl",
    tagline: "label-kicker mt-0.5 text-[#6CABDD]",
    gap: "gap-3",
  },
  md: {
    logo: "h-16 w-auto object-contain sm:h-20",
    name: "font-display text-xl font-black tracking-tight sm:text-2xl",
    tagline: "label-kicker mt-0.5 text-[#6CABDD] sm:text-sm",
    gap: "gap-3 sm:gap-4",
  },
  lg: {
    logo: "h-20 w-auto object-contain sm:h-24 md:h-28",
    name: "font-display text-2xl font-black tracking-tight sm:text-3xl md:text-4xl",
    tagline: "label-kicker mt-1 text-[0.85rem] text-[#6CABDD] sm:text-base",
    gap: "gap-4 sm:gap-5",
  },
  xl: {
    logo: "h-28 w-auto object-contain sm:h-32 md:h-40",
    name: "font-display text-3xl font-black tracking-tight sm:text-4xl md:text-5xl",
    tagline: "label-kicker mt-1.5 text-sm text-[#6CABDD] sm:text-base md:text-lg",
    gap: "gap-5 sm:gap-6",
  },
};

export default function Splash() {
  const copy = usePageContent("splash");
  const { theme } = useTheme();
  const logoSrc = theme.brand_logo_url
    ? resolveAsset(theme.brand_logo_url)
    : "";
  const brandName = theme.brand_name || "LAST";
  const brandTagline = theme.brand_tagline || "Let's All Sing Together";
  const sizes = SPLASH_HEADER_SIZES[theme.splash_header_size] || SPLASH_HEADER_SIZES.lg;
  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#0A0F17] text-white"
      data-testid="splash-page"
    >
      {/* Sky-blue accent rail */}
      <div
        className="absolute inset-y-0 left-0 w-2 bg-[#6CABDD]"
        aria-hidden="true"
      />

      {/* Soft glow behind the logo */}
      <div
        className="pointer-events-none absolute left-1/2 top-[35%] h-[40rem] w-[40rem] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
        style={{ background: "#6CABDD" }}
        aria-hidden="true"
      />

      <main className="relative z-10 mx-auto flex min-h-screen max-w-4xl flex-col items-start gap-12 px-6 py-12 sm:px-12">
        <header className={`flex items-center ${sizes.gap}`}>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={brandName}
              className={sizes.logo}
              data-testid="splash-brand-logo"
            />
          ) : null}
          <div className="flex flex-col leading-tight">
            <span className={sizes.name}>
              {brandName}
            </span>
            <span className={sizes.tagline}>
              {brandTagline}
            </span>
          </div>
        </header>

        <section className="py-10">
          <p className="label-kicker text-brand-accent" style={kickerStyle(copy)}>
            {copy.kicker}
          </p>
          <h1
            className="brand-headline mt-4 whitespace-pre-line font-display text-5xl font-black leading-[1.05] tracking-tight sm:text-7xl"
            style={titleStyle(copy)}
          >
            {copy.title}
          </h1>
          {copy.intro_rich ? (
            <div
              className="tiptap-prose splash-intro-rich mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
              data-testid="splash-intro-rich"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(copy.intro_rich, POST_HTML_PURIFY_CONFIG),
              }}
            />
          ) : copy.intro ? (
            <p className="mt-6 max-w-xl whitespace-pre-line text-base leading-relaxed text-white/70 sm:text-lg">
              {copy.intro}
            </p>
          ) : null}

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <span className="label-kicker text-white/50">Not affiliated with Coventry City FC</span>
            <span className="label-kicker text-white/50">·</span>
            <span className="label-kicker text-[#6CABDD]">#PUSB</span>
          </div>
        </section>

        <SplashContactForm />

        <footer className="flex w-full flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <span className="label-kicker text-white/50">
            Play up Sky Blues · 2026
          </span>
          <Link
            to="/admin/login"
            className="label-kicker text-white/50 transition-colors hover:text-[#6CABDD]"
            data-testid="splash-admin-link"
          >
            Staff login →
          </Link>
        </footer>
      </main>
    </div>
  );
}

function SplashContactForm() {
  const successCopy = usePageContent("splash_success");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("subject", "Splash page enquiry");
      fd.append("message", form.message.trim());
      await api.post("/contact", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section
        className="w-full max-w-2xl rounded-md border border-[#6CABDD]/30 bg-[#6CABDD]/10 p-6 text-center sm:p-8"
        data-testid="splash-contact-success"
      >
        <CheckCircle2 className="mx-auto text-[#6CABDD]" size={28} />
        <h2 className="mt-3 whitespace-pre-line font-display text-2xl font-black tracking-tight">
          {successCopy.title}
        </h2>
        {successCopy.intro ? (
          <p className="mt-1 text-sm text-white/70">
            {successCopy.intro}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => setDone(false)}
          className="mt-4 label-kicker text-[#6CABDD] underline-offset-4 hover:underline"
          data-testid="splash-contact-another"
        >
          Send another
        </button>
      </section>
    );
  }

  return (
    <section
      className="w-full max-w-2xl border-t border-white/10 pt-10"
      data-testid="splash-contact-form"
    >
      <div className="flex items-center gap-2 text-[#6CABDD]">
        <Mail size={14} strokeWidth={2.5} />
        <span className="label-kicker">Want a heads-up at launch?</span>
      </div>
      <h2 className="mt-2 font-display text-2xl font-black tracking-tight sm:text-3xl">
        Drop us a line.
      </h2>
      <p className="mt-2 max-w-xl text-sm text-white/60">
        Want to share a tip, get listed in the Links directory, or just say
        hello? Send a message and we'll come back to you when the doors open.
      </p>

      <form
        onSubmit={submit}
        className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <SplashField
          placeholder="Your name"
          value={form.name}
          onChange={update("name")}
          required
          testid="splash-contact-name"
        />
        <SplashField
          placeholder="Your email"
          type="email"
          value={form.email}
          onChange={update("email")}
          required
          testid="splash-contact-email"
        />
        <textarea
          rows={4}
          required
          maxLength={2000}
          placeholder="Your message"
          value={form.message}
          onChange={update("message")}
          data-testid="splash-contact-message"
          className="sm:col-span-2 block w-full rounded-md border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-[#6CABDD]"
        />
        {error ? (
          <p
            className="sm:col-span-2 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/10 px-3 py-2 text-sm text-[#FFA8A8]"
            data-testid="splash-contact-error"
          >
            {error}
          </p>
        ) : null}
        <div className="sm:col-span-2 flex items-center justify-between gap-3">
          <span className="label-kicker text-white/40">
            Goes straight to the LAST inbox
          </span>
          <button
            type="submit"
            disabled={submitting}
            data-testid="splash-contact-submit"
            className="inline-flex items-center gap-2 rounded-md bg-[#6CABDD] px-5 py-2.5 font-display text-sm font-black uppercase tracking-wider text-[#0A0F17] transition-colors hover:bg-white disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} strokeWidth={2.5} />
            )}
            {submitting ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </section>
  );
}

function SplashField({ placeholder, value, onChange, required, type, testid }) {
  return (
    <input
      type={type || "text"}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="block w-full rounded-md border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-[#6CABDD]"
    />
  );
}
